// The brokerage's colour, applied to the app they work in all day.
//
// ── WHY THIS EXISTS (tracker row 41) ────────────────────────────────────────────────
// Rize set a brand colour and a logo and reported that only the client portal changed.
// They were right: `organizations.brand.brand_color` had exactly three readers — the
// portal, the CMA PDF, and the settings screen that edits it — and the agent-facing app
// was hardcoded to the RizeOS default. "Import company color to theme the app" was the
// ask; the app was the half that never happened.
//
// ── WHY A CSS VARIABLE AND NOT A REBUILD ────────────────────────────────────────────
// Tailwind v4 declares the palette as custom properties in an `@theme` block, so every
// `bg-primary` in the app already resolves through `--color-primary` at paint time.
// Overriding that one variable on the document root re-themes everything that was built
// correctly, with no rebuild and no per-component work. Components that hardcoded the hex
// instead do NOT follow, which is why those were swept in the same change.
//
// ── CONTRAST IS NOT OPTIONAL HERE ───────────────────────────────────────────────────
// The accent is a BUTTON colour: text sits on top of it. The app's default foreground is
// near-white, which is unreadable on a pale brand colour — and a brokerage picking a
// yellow is not a hypothetical, it is a colour picker with no rules on it. So the
// foreground is COMPUTED from the chosen colour rather than assumed, and the settings
// screen warns before a colour is saved that cannot carry legible text.
//
// PURE MODULE — no React, no DOM — so the maths is unit-testable on its own.

/** The platform default, and the fallback whenever an org has set nothing valid. */
export const DEFAULT_BRAND_COLOR = '#E11D48'

/** Foregrounds the app already uses. The accent's text is one of these two, never a blend. */
const LIGHT_FOREGROUND = '#FAFAFA'
const DARK_FOREGROUND = '#09090B'

/**
 * The two grounds the app itself paints, from the `@theme` blocks in index.css.
 *
 * ⚠️ BOTH, NOT ONE. The app ships a dark theme and a light one, and an accent need only
 * fail against one of them to disappear on that theme.
 */
const APP_BACKGROUND_DARK = '#09090B'
const APP_BACKGROUND_LIGHT = '#FFFFFF'

/**
 * WCAG AA for large text and non-text UI. The accent appears at heading sizes, on icons
 * and on buttons, so 3.0 is the applicable floor rather than the 4.5 body copy needs.
 */
export const MIN_ACCENT_CONTRAST = 3.0

/** #RGB or #RRGGBB to [r, g, b] 0-255, or null when it is not a hex colour. */
export function parseHexColor(value: string | null | undefined): [number, number, number] | null {
  if (typeof value !== 'string') return null
  const hex = value.trim().replace(/^#/, '')
  if (!/^[0-9a-fA-F]{3}$/.test(hex) && !/^[0-9a-fA-F]{6}$/.test(hex)) return null
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

/** WCAG relative luminance. The 0.03928 branch is the sRGB gamma curve, not a fudge. */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (c: number): number => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio between two colours, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number | null {
  const ca = parseHexColor(a)
  const cb = parseHexColor(b)
  if (ca === null || cb === null) return null
  const la = relativeLuminance(ca)
  const lb = relativeLuminance(cb)
  const light = Math.max(la, lb)
  const dark = Math.min(la, lb)
  return (light + 0.05) / (dark + 0.05)
}

/**
 * The readable text colour to sit ON this accent.
 *
 * Picks whichever of the app's two foregrounds contrasts better, rather than assuming the
 * near-white default. On the platform's own rose this returns the near-white it always
 * used; on a pale brand colour it returns the near-black, which is the whole point.
 */
export function foregroundFor(accent: string): string {
  const onLight = contrastRatio(accent, LIGHT_FOREGROUND) ?? 0
  const onDark = contrastRatio(accent, DARK_FOREGROUND) ?? 0
  return onDark > onLight ? DARK_FOREGROUND : LIGHT_FOREGROUND
}

/**
 * Is this colour usable as the accent — is it legible AS TEXT on both of the app's grounds?
 *
 * ⚠️ THE FIRST VERSION OF THIS ASKED THE WRONG QUESTION AND COULD NEVER FAIL.
 * It asked whether either foreground could sit ON the accent — and because those two
 * foregrounds are near-white and near-black, one of them always clears 3.0. Measured
 * across the whole grey axis, the worst case is 4.38, so the check refused nothing and the
 * warning it drove could never appear. A guard no input can trip is worse than no guard:
 * it reads as protection.
 *
 * The question that matters is the other direction. The accent is used AS TEXT — 536
 * `text-primary` usages across the app — and it has to survive on BOTH grounds the app
 * paints. That check refuses exactly the plausible bad picks:
 *
 *     #0A0A0A  near-black   1.00 on the dark theme    invisible
 *     #FFFFFF  white        1.00 on the light theme   invisible
 *     #FFEB3B  bright yellow 1.22 on the light theme  invisible
 *
 * and passes the ones that should pass — the platform's #E11D48 (worst 4.24) and Rize's
 * own #5C706E (worst 3.79).
 *
 * The other direction still matters and is still handled: text sitting ON the accent is
 * solved by foregroundFor flipping the foreground, which is a fix rather than a refusal.
 */
export function accentIsLegible(accent: string): boolean {
  const onDarkTheme = contrastRatio(accent, APP_BACKGROUND_DARK)
  const onLightTheme = contrastRatio(accent, APP_BACKGROUND_LIGHT)
  if (onDarkTheme === null || onLightTheme === null) return false
  return Math.min(onDarkTheme, onLightTheme) >= MIN_ACCENT_CONTRAST
}

/** The brand colour out of an org's `brand` jsonb, or null when unset or malformed. */
export function readBrandColor(brand: unknown): string | null {
  if (typeof brand !== 'object' || brand === null) return null
  const value = (brand as { brand_color?: unknown }).brand_color
  if (typeof value !== 'string') return null
  return parseHexColor(value) === null ? null : value.trim()
}

/**
 * The brokerage's logo out of the same jsonb — the OTHER half of tracker row 41
 * ("import company color to theme the app + logo"). The colour reached the agent
 * app on 2026-09-01; the logo still only ever rendered on the client portal and
 * the CMA PDF, so the sidebar an agent looks at all day said "rize." under a
 * brokerage that had uploaded its own mark.
 *
 * https only: the value is rendered as an <img src> in the app shell, and the
 * CSP's img-src allows the storage host the settings uploader writes to, not
 * arbitrary schemes. Anything else reads as "no logo" and the wordmark stands.
 */
export function readBrandLogoUrl(brand: unknown): string | null {
  if (typeof brand !== 'object' || brand === null) return null
  const value = (brand as { logo_url?: unknown }).logo_url
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return /^https:\/\/\S+$/i.test(trimmed) ? trimmed : null
}

/**
 * Apply an organisation's accent to the document, or clear it back to the platform's.
 *
 * ⚠️ CLEARING IS AS IMPORTANT AS SETTING. These are inline properties on the root element
 * and they outlive a React unmount, so an org switch from a branded brokerage to an
 * unbranded one must actively remove them — otherwise the previous org's colour follows
 * the user into the next one, which on a shared machine looks like a data leak even though
 * it is only paint.
 *
 * A colour that cannot carry legible text is REFUSED here as well as warned about in
 * settings: a stored value predating the check, or one written straight to the database,
 * must not be able to render an unusable app.
 */
export function applyBrandTheme(
  accent: string | null,
  root: { style: { setProperty(k: string, v: string): void; removeProperty(k: string): void } },
): void {
  const usable = accent !== null && parseHexColor(accent) !== null && accentIsLegible(accent)
  if (!usable) {
    root.style.removeProperty('--color-primary')
    root.style.removeProperty('--color-primary-foreground')
    root.style.removeProperty('--color-ring')
    return
  }
  root.style.setProperty('--color-primary', accent)
  root.style.setProperty('--color-primary-foreground', foregroundFor(accent))
  // The focus ring is the accent everywhere in this app's `@theme`; leaving it on the
  // platform rose would put a stray pink outline on a brokerage's own buttons.
  root.style.setProperty('--color-ring', accent)
}
